import type { AnyFieldApi } from '@tanstack/react-form';

import { cn } from 'cn';
import { useCallback, useMemo } from 'react';

import { fetchUploadLink } from '~/data/file';

import { isFieldRequired, unique } from '@vestigas/utils';

import type {
  FilesListItem,
  FileUploadRestrictions,
  OnAfterUploadFn,
  UploadSuccessResult,
} from '@vestigas/ui';
import { EmptyState, FilesList, Icon, Uploader } from '@vestigas/ui';
import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

/**
 * The shape stored in the form field for each uploaded file.
 * Matches the contract attachment API format.
 */
export type StoredFile = {
  fileId: string;
  fileMime: string;
  fileName: string;
};

type ArrayFieldApi = AnyFieldApi & {
  pushValue: (value: StoredFile) => void;
  removeValue: (index: number) => void;
};

type FileFieldProps = {
  readonly emptyStateHint?: string;
  readonly hint?: string;
  readonly isDisabled?: boolean;
  readonly label?: string;
  readonly onAfterUpload?: OnAfterUploadFn;
  readonly restrictions?: FileUploadRestrictions;
} & ComponentStyling;

/**
 * File upload field for TanStack Form.
 *
 * Uses the Uploader component to handle file uploads to Azure Blob Storage.
 * Stores uploaded files as `{ fileId, fileName, fileMime }` in the field value.
 * Requires the parent field to be set up with `mode="array"`.
 */
export const FileField = ({
  className,
  emptyStateHint,
  hint,
  isDisabled,
  label,
  onAfterUpload,
  restrictions,
  style,
}: FileFieldProps) => {
  const field = useFieldContext<StoredFile[]>() as ArrayFieldApi;

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const selectedFiles = useMemo(
    () => (field.state.value ?? []) as StoredFile[],
    [field.state.value],
  );

  const hasErrors = Boolean(field.state.meta.errors?.length);

  const handleSuccess = useCallback(
    (result: UploadSuccessResult) => {
      for (const { fileId, fileName, type: fileMime } of result) {
        field.pushValue({
          fileId,
          fileMime,
          fileName,
        });
      }
    },
    [field],
  );

  const handleRemove = useCallback(
    (item: FilesListItem) => {
      const index = selectedFiles.findIndex((f) => f.fileId === item.id);
      if (index >= 0) {
        field.removeValue(index);
      }
    },
    [field, selectedFiles],
  );

  return (
    <div className={cn('relative', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <Uploader
        getUploadLink={fetchUploadLink}
        isDisabled={isDisabled}
        onAfterUpload={onAfterUpload}
        onSuccess={handleSuccess}
        restrictions={restrictions}
      />
      {Boolean(selectedFiles?.length) && (
        <FilesList
          actions={[
            {
              icon: <Icon metaphor="close" size="sm" />,
              id: 'remove',
              onClick: handleRemove,
            },
          ]}
          className="mt-2"
          items={selectedFiles.map((file) => ({
            id: file.fileId,
            name: file.fileName,
            type: file.fileMime,
          }))}
        />
      )}
      {!selectedFiles?.length && Boolean(emptyStateHint) && (
        <EmptyState
          className="mt-6"
          hint={emptyStateHint}
          Illustration={null}
        />
      )}
      {hasErrors ? (
        <ValidationError>
          {unique(field.state.meta.errors.map(({ message }) => message)).join(
            ', ',
          )}
        </ValidationError>
      ) : null}
    </div>
  );
};
